import * as React from "react"
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area"

import { cn } from "@/lib/utils"

type ScrollAreaViewportElement = React.ElementRef<typeof ScrollAreaPrimitive.Viewport>
type ScrollAreaRootProps = React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
type MutationObserverLike = {
  observe(target: Node, options?: MutationObserverInit): void
  disconnect(): void
}
type ResizeObserverLike = {
  observe(target: Element): void
  disconnect(): void
}

const scrollToBottom = (
  container: ScrollAreaViewportElement | null,
  smooth = false,
) => {
  if (!container) return

  if (smooth && typeof container.scrollTo === "function") {
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" })
    return
  }

  container.scrollTop = container.scrollHeight
}

type CustomScrollAreaProps = {
  autoScrollToBottom?: boolean
}

const ScrollAreaAuto = React.forwardRef<
  ScrollAreaViewportElement,
  CustomScrollAreaProps & ScrollAreaRootProps
>(function ScrollAreaAuto(
  { className, children, autoScrollToBottom, ...props },
  forwardedRef,
) {
  const viewportRef = React.useRef<ScrollAreaViewportElement | null>(null)

  React.useImperativeHandle(
    forwardedRef,
    () => viewportRef.current as ScrollAreaViewportElement,
    [],
  )

  React.useEffect(() => {
    if (!autoScrollToBottom) return

    const scrollViewport = (smooth = true) => {
      scrollToBottom(viewportRef.current, smooth)
    }

    const initialFrame =
      typeof window !== "undefined"
        ? window.requestAnimationFrame(() => scrollViewport(false))
        : null

    let mutationObserver: MutationObserverLike | null = null
    if (
      typeof window !== "undefined" &&
      "MutationObserver" in window &&
      viewportRef.current
    ) {
      mutationObserver = new window.MutationObserver(() => {
        scrollViewport(true)
      })
      mutationObserver.observe(viewportRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      })
    }

    let resizeObserver: ResizeObserverLike | null = null
    if (
      typeof window !== "undefined" &&
      "ResizeObserver" in window &&
      viewportRef.current
    ) {
      resizeObserver = new window.ResizeObserver(() => {
        scrollViewport(true)
      })
      resizeObserver.observe(viewportRef.current)
    }

    return () => {
      if (initialFrame !== null) {
        window.cancelAnimationFrame(initialFrame)
      }
      mutationObserver?.disconnect()
      resizeObserver?.disconnect()
    }
  }, [autoScrollToBottom])

  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        className="focus-visible:ring-ring/50 size-full rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1"
        ref={viewportRef}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBarAuto />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  )
})

ScrollAreaAuto.displayName = "ScrollAreaAuto"

function ScrollBarAuto({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none p-px transition-colors select-none",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  )
}

export { ScrollAreaAuto, ScrollBarAuto }
